import React from 'react';

interface IModalContext {
  actionsRef: React.RefObject<HTMLDivElement | null>
  headerRef: React.RefObject<HTMLDivElement>
  modalRef: React.RefObject<HTMLDivElement>
}

const ModalContext = React.createContext<IModalContext>(null);

export function useModalContext() {
  const context = React.useContext(ModalContext);

  if (!context) {
    throw new Error('useModalContext should be used in a modal context.');
  }

  React.useEffect(() => {
    const header = context.headerRef.current;
    const modal = context.modalRef.current;
    const actions = context.actionsRef.current;

    if (!header || !modal) {
      return;
    }

    function setOffset() {
      const headerHeight = header?.getBoundingClientRect().height;
      const actionsHeight = actions?.getBoundingClientRect()?.height;

      if (headerHeight) {
        modal.style.scrollPaddingTop = `${headerHeight}px`;
      }

      if (actionsHeight) {
        modal.style.scrollPaddingBottom = `${actionsHeight}px`;
      }
    }

    setOffset();

    window.addEventListener('resize', setOffset);

    // eslint-disable-next-line consistent-return
    return () => {
      window.removeEventListener('resize', setOffset);
      modal.style.scrollPaddingTop = '0px';
    };
  }, [context.headerRef, context.modalRef, context.actionsRef]);

  return context;
}

export default function withModalContext<T>(WrappedComponent: React.ComponentType<T>) {
  return (props: T) => {
    const actionsRef = React.useRef<HTMLDivElement>();
    const headerRef = React.useRef<HTMLDivElement>();
    const modalRef = React.useRef<HTMLDivElement>();

    const providerValues = React.useMemo(() => ({
      actionsRef,
      headerRef,
      modalRef,
    }), [actionsRef, headerRef, modalRef]);

    return (
      <ModalContext.Provider value={providerValues}>
        <WrappedComponent {...props} />
      </ModalContext.Provider>
    );
  };
}
